import * as React from 'react';

import { connect, useSelector, useDispatch } from 'react-redux';
import { Switch, Route, useRouteMatch, Redirect } from 'react-router-dom';

import AppState from '@webapp/store/state';

import ReleaseInfoTitlePage from './release-info-title';
import ReleaseInfoDetailsPage from './release-info-detail';
import ReleaseInfoDatePage from './release-info-date';
import ReleasePreviewPage from './release-preview';
import ReleaseTrackInfoPage from './release-track-info';
import PaymentSuccess from './payment-success';
import PaymentPage from './payment';
import PublishedPage from './published';

import StreamingPlatformsPage from './streaming-platforms';

import ReleaseSidebarCtrl from '@webapp/controllers/release/release-sidebar';
import SidebarContainer from '@components/layout/sidebar-container';
import FormContainer from '@components/layout/form-container';
import SidebarWrapper from '@components/layout/sidebar-wrapper';
import ReleaseMenuMobile from '@webapp/controllers/release/release-menu-mobile';
import styled from 'styled-components';
import SmallNotification from '@components/small-notification';
import { Translate } from 'react-localize-redux';
import { ResetReleaseStore } from '@webapp/store/release/action-creators';
import { ResetTracksStore } from '@webapp/store/tracks/action-creators';
import { ClearAssets } from '@webapp/store/assets/action-creators';
import { ClearCollaboratorState } from '@webapp/store/collaborator/action-creators';
import { ClearOwnershipState } from '@webapp/store/ownership/action-creators';

interface ReleasePageProps {
  className?: string;
  isLaptop?: boolean;
  isMobile?: boolean;
}

const mapStateToProps = (state: AppState) => ({
  isLaptop: state.ui.laptop,
  isMobile: state.ui.mobile,
});

export const ReleasePage: React.FC<ReleasePageProps> = ({
  className,
  isLaptop,
  isMobile,
}) => {
  const match = useRouteMatch<{ account: string }>();

  const dispatch = useDispatch();

  const isPreview = useRouteMatch({
    path: `${match.path}/:artistId?/:releaseId?/preview`,
    strict: true,
    sensitive: true,
  });

  const isPublished = useRouteMatch({
    path: `${match.path}/:artistId?/:releaseId?/published`,
  });

  const isCheckout = useRouteMatch({
    path: `${match.path}/:artistId?/:releaseId?/checkout`,
  });

  const showSidebar = !(isPreview || isPublished || isCheckout);

  const toastNotification = useSelector(
    (state: AppState) => state.ui.toastNotification
  );
  React.useEffect(() => {
    //clear all release related redux state when leaving the page
    return () => {
      dispatch(new ResetReleaseStore());
      dispatch(new ResetTracksStore());
      dispatch(new ClearAssets());
      dispatch(new ClearCollaboratorState());
      dispatch(new ClearOwnershipState());
    };
  }, [dispatch]);

  return (
    <div className={className}>
      <SidebarContainer>
        {showSidebar ? (
          <SidebarFixed>
            <SidebarWrapper hasScoll>
              <ReleaseSidebarCtrl />
            </SidebarWrapper>
          </SidebarFixed>
        ) : null}
        {showSidebar && isMobile && <ReleaseMenuMobile />}
        <FormContainer
          wider={!!isPublished?.isExact || (!!isPreview && isLaptop)}
          hasSidebar={!isCheckout}
        >
          <Switch>
            <Route
              exact
              path={`${match.path}/:artistId?/:releaseId?/title`}
              render={() => <ReleaseInfoTitlePage />}
            />
            <Route
              exact
              path={`${match.path}/:artistId?/:releaseId?/details`}
              render={() => <ReleaseInfoDetailsPage />}
            />
            <Route
              exact
              path={`${match.path}/:artistId?/:releaseId?/dates`}
              render={() => <ReleaseInfoDatePage />}
            />
            <Route
              exact
              path={`${match.path}/:artistId?/:releaseId?/preview`}
              render={() => <ReleasePreviewPage />}
            />
            <Route
              exact
              path={`${match.path}/:artistId?/:releaseId?/track/:trackId?`}
              render={() => <ReleaseTrackInfoPage />}
            />
            <Route
              exact
              path={`${match.path}/:artistId?/:releaseId?/checkout/:type?`}
              render={() => <PaymentPage />}
            />
            <Route
              exact
              path={`${match.path}/:artistId?/:releaseId?/payment-success`}
              render={() => <PaymentSuccess />}
            />
            <Route
              exact
              path={`${match.path}/:artistId?/:releaseId?/published`}
              render={() => <PublishedPage />}
            />
            <Route
              exact
              path={`${match.path}/:artistId?/:releaseId?/streaming-platforms`}
              render={() => <StreamingPlatformsPage />}
            />
            <Route
              exact
              path={match.path}
              render={() => <Redirect to={`${match.path}/title`} />}
            />
          </Switch>
        </FormContainer>
      </SidebarContainer>
      {(toastNotification?.value === 'invite_sent' ||
        toastNotification?.value === 'invite_error') && (
        <SmallNotification
          notificationType={toastNotification.type}
          title={<Translate id={toastNotification.label} />}
        />
      )}
    </div>
  );
};

export default connect(mapStateToProps)(ReleasePage);

const SidebarFixed = styled.div`
  position: fixed;
`;
